import { parseFilterModel } from '@/lib/createAgGridDatasource';
import type { InstrumentRunsDownloadOptions } from '@/services/instrumentRuns';
import type { InstrumentRunFilters, InstrumentRunSortField } from '@/types';

// Only the InstrumentRunSortField values that are also grid columns here (e.g. 'code' isn't shown).
// Typed against InstrumentRunSortField so a renamed/dropped enum member fails type-check here
// instead of an unchecked cast at the call site silently sending a stale value.
export const VALID_SORT_FIELDS: readonly InstrumentRunSortField[] = [
  'name',
  'description',
  'status',
  'species',
  'run_qc_outcome',
  'created_at',
  'updated_at',
  'submitted_at',
  'completed_at',
  'created_by_full_name',
  'updated_by_full_name',
  'submitted_by_full_name',
];

export function isValidSortField(colId: string | undefined): colId is InstrumentRunSortField {
  return (VALID_SORT_FIELDS as readonly string[]).includes(colId ?? '');
}

/** The subset of AG Grid's `ColumnState` that `buildDownloadOptions` reads. */
export interface SortColumnState {
  colId: string;
  sort?: 'asc' | 'desc' | null;
  sortIndex?: number | null;
}

/**
 * Pure translation of the grid's current sort/filter state into the CSV export's options.
 * Kept out of the route component so the sort-field gate, the primary-sort selection, and the
 * filters-length branch can be unit tested without mounting the grid.
 */
export function buildDownloadOptions(
  columnState: readonly SortColumnState[],
  filterModel: Parameters<typeof parseFilterModel>[0],
): InstrumentRunsDownloadOptions {
  // Sort by sortIndex, not column order, so a multi-sort's primary column (not just the leftmost
  // sorted one) drives the export — matters once multi-sort is enabled on this grid.
  const sortedColumn = columnState
    .filter(col => col.sort)
    .toSorted((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))[0];
  const filters = parseFilterModel(filterModel);

  return {
    sortBy: isValidSortField(sortedColumn?.colId) ? sortedColumn.colId : undefined,
    sortOrder: sortedColumn?.sort ?? undefined,
    filters: Object.keys(filters).length > 0 ? (filters as InstrumentRunFilters) : null,
  };
}
